import { useMemo, useCallback } from "react"
import { I18nIndividual as GedcomIndividual } from "../model/gedcom-individual"
import { I18nFamily as GedcomFamily } from "../model/gedcom-family"
import { useIntl } from "react-intl"


export function useRelatedDeps(individualsData: GedcomIndividual[], familiesData?: GedcomFamily[]) {
    const intl = useIntl()

    /**
     * Compute individualsMap and fullNames in a single iteration.
     */
    const { individualsMap, fullNames } = useMemo(() => {
        const map = new Map<string, GedcomIndividual>()
        const names: Record<string, string> = {}
        individualsData.forEach(i => {
            map.set(i.id, i)
            names[i.id] = GedcomIndividual.getFullName(i, intl.locale, false) ?? i.id
        })
        return { individualsMap: map, fullNames: names }
    }, [individualsData, intl.locale])

/* ------------------------------------------------------------------------------------------------------------------ */
/* INDIVIDUAL DEPS                                                                                                    */
/* ------------------------------------------------------------------------------------------------------------------ */

    const childrenMap = useMemo(() => {
        const map: Record<string, GedcomIndividual[]> = {}
        individualsData.forEach(i => {
            if (i.fatherId) map[i.fatherId] ??= []
            if (i.motherId) map[i.motherId] ??= []
            i.fatherId && map[i.fatherId].push(i)
            i.motherId && map[i.motherId].push(i)
        })
        return map
    }, [individualsData])

    const spousesMap = useMemo(() => {
        const map: Record<string, GedcomFamily[]> = {}
        familiesData?.forEach(f => {
            if (f.husbandId) map[f.husbandId] ??= []
            if (f.husbandId) map[f.husbandId].push(f)
            if (f.wifeId) map[f.wifeId] ??= []
            if (f.wifeId) map[f.wifeId].push(f)
        })
        return map
    }, [familiesData])

/* ------------------------------------------------------------------------------------------------------------------ */
/* FAMILY DEPS                                                                                                        */
/* ------------------------------------------------------------------------------------------------------------------ */

    const husbandName = useCallback((row: GedcomFamily) => {
        if (!row.husbandId) return undefined
        const husband = individualsMap.get(row.husbandId)
        return husband ? GedcomIndividual.getFullName(husband, intl.locale, false) ?? husband.id : row.husbandId
    }, [individualsMap, intl.locale])

    const wifeName = useCallback((row: GedcomFamily) => {
        if (!row.wifeId) return undefined
        const wife = individualsMap.get(row.wifeId)
        return wife ? GedcomIndividual.getFullName(wife, intl.locale, false) ?? wife.id : row.wifeId
    }, [individualsMap, intl.locale])

    return { individualsMap, childrenMap, spousesMap, fullNames, husbandName, wifeName }
}
